import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  RefreshCw,
  Sun,
  Moon
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const OccupancyCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDetails, setDayDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      // Get first and last day of month
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      const startDate = firstDay.toISOString().split("T")[0];
      const endDate = lastDay.toISOString().split("T")[0];

      // Inventory loads faster, calendar can take up to 60s from Oscar
      const [inventoryRes, calendarRes] = await Promise.all([
        axios.get(`${API}/redforts/inventory`, { timeout: 30000 }),
        axios.get(`${API}/redforts/calendar?start=${startDate}&end=${endDate}`, { timeout: 90000 })
      ]);

      if (inventoryRes.data.success) {
        setInventory(inventoryRes.data.data.accommodations || []);
      }

      if (calendarRes.data.success) {
        // Data is already structured by date from backend
        setCalendarData(calendarRes.data.data || {});
      }
    } catch (error) {
      console.error("Error fetching calendar:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("La conexión con Oscar tardó demasiado. Intenta de nuevo.");
      } else {
        toast.error("Error al cargar calendario");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDayDetails = async (date) => {
    setLoadingDetails(true);
    try {
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);
      
      const arrival = date.toISOString().split("T")[0];
      const departure = nextDay.toISOString().split("T")[0];

      const response = await axios.get(`${API}/redforts/availability?arrival=${arrival}&departure=${departure}`);
      
      if (response.data.success) {
        const options = response.data.data.options || [];
        
        // Group by accommodation
        const byAcco = {};
        options.forEach(opt => {
          const accoId = opt.acco_id;
          if (!byAcco[accoId]) {
            byAcco[accoId] = {
              available: 0,
              minPrice: Infinity,
              maxPrice: 0,
              rates: []
            };
          }
          byAcco[accoId].available = Math.max(byAcco[accoId].available, opt.amount || 0);
          byAcco[accoId].minPrice = Math.min(byAcco[accoId].minPrice, opt.price || Infinity);
          byAcco[accoId].maxPrice = Math.max(byAcco[accoId].maxPrice, opt.price || 0);
          byAcco[accoId].rates.push({
            price: opt.price,
            rateId: opt.periods?.[0]?.rate_id
          });
        });

        setDayDetails({
          date: arrival,
          byAcco,
          totalOptions: options.length
        });
      }
    } catch (error) {
      console.error("Error fetching day details:", error);
      toast.error("Error al cargar detalles del día");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDayClick = (day) => {
    const clickedDate = new Date(year, month, day);
    setSelectedDay(clickedDate);
    
    // Get data from calendar data
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const dayData = calendarData[dateStr];
    
    if (dayData && dayData.by_acco) {
      setDayDetails({
        date: dateStr,
        byAcco: dayData.by_acco,
        totalRooms: dayData.total_rooms,
        totalAvailable: dayData.total_available,
        totalOccupied: dayData.total_occupied,
        occupancyRate: dayData.occupancy_rate
      });
      setLoadingDetails(false);
    } else {
      // Fetch from availability API if no calendar data
      fetchDayDetails(clickedDate);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(year, month + direction, 1);
    // Limit to Dec 2026
    if (newDate <= new Date(2026, 11, 31)) {
      setCurrentDate(newDate);
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  useEffect(() => {
    fetchCalendarData();
  }, [year, month]);

  // Generate calendar days
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDay = (firstDayOfMonth.getDay() + 6) % 7; // Monday = 0

  const calendarDays = [];
  
  // Empty cells for days before month starts
  for (let i = 0; i < startingDay; i++) {
    calendarDays.push(null);
  }
  
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  const getOccupancyColor = (available, total, hasData) => {
    // Si no hay datos, mostrar gris neutro
    if (!hasData || total === 0) return "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300";
    const rate = ((total - available) / total) * 100;
    if (rate >= 90) return "bg-red-500 text-white";
    if (rate >= 70) return "bg-orange-500 text-white";
    if (rate >= 50) return "bg-amber-400 text-black";
    if (rate >= 30) return "bg-emerald-400 text-black";
    return "bg-emerald-200 dark:bg-emerald-900 text-emerald-900 dark:text-emerald-100";
  };

  const totalRooms = inventory.reduce((sum, acco) => sum + (acco.amount || 0), 0);

  const getAccoName = (accoId) => {
    const acco = inventory.find(a => a.id === parseInt(accoId));
    return acco?.name || `Tipo ${accoId}`;
  };

  const getAccoTotal = (accoId) => {
    const acco = inventory.find(a => a.id === parseInt(accoId));
    return acco?.amount || 0;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Calendario de Ocupación</h1>
          <p className="text-muted-foreground">Vista de disponibilidad por día hasta Diciembre 2026</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={goToToday}>
            Hoy
          </Button>
          <Button variant="outline" onClick={fetchCalendarData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Month Navigation */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-4">
              <Select
                value={String(month)}
                onValueChange={(value) => setCurrentDate(new Date(year, parseInt(value), 1))}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => (
                    <SelectItem key={`month-${i}`} value={String(i)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={String(year)}
                onValueChange={(value) => setCurrentDate(new Date(parseInt(value), month, 1))}
              >
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Legend */}
          <div className="flex flex-wrap gap-2 mb-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-red-500"></div>
              <span>90%+ ocupado</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-orange-500"></div>
              <span>70-90%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-amber-400"></div>
              <span>50-70%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-emerald-400"></div>
              <span>30-50%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-emerald-200 dark:bg-emerald-900"></div>
              <span>&lt;30% ocupado</span>
            </div>
          </div>

          {/* Calendar Grid */}
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="spinner w-8 h-8" />
              <p className="text-muted-foreground text-sm">Cargando datos de Oscar... puede tardar hasta 1 minuto</p>
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {/* Weekday headers */}
              {WEEKDAYS.map((day) => (
                <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {calendarDays.map((day, index) => {
                if (day === null) {
                  return <div key={`empty-${index}`} className="aspect-square"></div>;
                }
                
                const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const dayData = calendarData[dateStr];
                const hasData = !!dayData;
                const available = dayData?.total_available || 0;
                const total = dayData?.total_rooms || totalRooms;
                const occupancyRate = dayData?.occupancy_rate || 0;
                const isToday = new Date().toISOString().split("T")[0] === dateStr;
                const isPast = new Date(dateStr) < new Date(new Date().toISOString().split("T")[0]);
                const isSelected = selectedDay && selectedDay.toISOString().split("T")[0] === dateStr;
                
                return (
                  <button
                    key={day}
                    onClick={() => handleDayClick(day)}
                    className={`
                      aspect-square p-1 rounded-lg text-center transition-all flex flex-col items-center justify-center
                      ${getOccupancyColor(available, total, hasData)}
                      ${isToday ? "ring-2 ring-primary ring-offset-2" : ""}
                      ${isSelected ? "ring-2 ring-blue-500 ring-offset-2" : ""}
                      ${isPast ? "opacity-50" : "hover:scale-105"}
                    `}
                    data-testid={`day-${dateStr}`}
                  >
                    <div className="text-sm font-bold">{day}</div>
                    {dayData ? (
                      <>
                        <div className="text-xs font-semibold">
                          {`${occupancyRate.toFixed(0)}%`}
                        </div>
                        <div className="text-[10px] opacity-90">
                          {dayData.total_occupied || 0}/{total}
                        </div>
                      </>
                    ) : (
                      <div className="text-xs opacity-70">-</div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Day Details Panel */}
      {selectedDay && (
        <Card data-testid="day-details">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              {selectedDay.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingDetails ? (
              <div className="flex items-center justify-center h-32">
                <div className="spinner w-6 h-6" />
              </div>
            ) : dayDetails ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-4 p-4 bg-muted rounded-lg">
                  <div className="text-center">
                    <p className="text-2xl font-bold">{dayDetails.totalRooms || totalRooms}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-600">{dayDetails.totalAvailable || 0}</p>
                    <p className="text-xs text-muted-foreground">Disponibles</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{dayDetails.totalOccupied || 0}</p>
                    <p className="text-xs text-muted-foreground">Ocupadas</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{dayDetails.occupancyRate?.toFixed(1) || 0}%</p>
                    <p className="text-xs text-muted-foreground">Ocupación</p>
                  </div>
                </div>

                {/* By room type */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {dayDetails.byAcco && Object.entries(dayDetails.byAcco).map(([accoId, accoData]) => {
                    const occupancyRate = accoData.occupancy_rate || 0;
                    
                    return (
                      <div 
                        key={accoId} 
                        className={`p-3 rounded-lg border ${
                          occupancyRate >= 80 ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800" :
                          occupancyRate >= 50 ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800" :
                          "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-sm">{accoData.name}</p>
                          </div>
                          <Badge variant={occupancyRate >= 80 ? "destructive" : occupancyRate >= 50 ? "default" : "secondary"}>
                            {occupancyRate.toFixed(0)}%
                          </Badge>
                        </div>
                        <div className="mt-2 flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">
                            {accoData.occupied}/{accoData.total} ocupadas
                          </span>
                          <span className="font-medium text-emerald-600">
                            {accoData.available} disp
                          </span>
                        </div>
                        {/* Progress bar */}
                        <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${
                              occupancyRate >= 80 ? "bg-red-500" :
                              occupancyRate >= 50 ? "bg-amber-500" :
                              "bg-emerald-500"
                            }`}
                            style={{ width: `${occupancyRate}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {(!dayDetails.byAcco || Object.keys(dayDetails.byAcco).length === 0) && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Sun className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay datos de disponibilidad para este día</p>
                    <p className="text-sm">Puede estar cerrado a ventas online</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">Selecciona un día para ver detalles</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold">{totalRooms}</p>
            <p className="text-sm text-muted-foreground">Total Habitaciones</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold">{inventory.length}</p>
            <p className="text-sm text-muted-foreground">Tipos de Habitación</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold">{MONTHS[month]}</p>
            <p className="text-sm text-muted-foreground">{year}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold">{daysInMonth}</p>
            <p className="text-sm text-muted-foreground">Días en el mes</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OccupancyCalendar;
